import { useAddExerciseToPatient } from '_api/mutations';
import { AddExerciseToPatientPayload, Exercise } from '_api/types';
import { SEVERITY } from '_constants/derived';
import DefaultAddExerciseToPatientSettings from '_constants/object-templates/DefaultExerciseToPatientSettings';
import { AlertMessage, BaseModal } from 'components/molecules';
import { AddExerciseToPatientForm } from 'components/organisms/forms';
import { normalizePatientInstructions } from 'components/organisms/forms/patientInstructions';
import React, { useState } from 'react';

import { buildExercisePayload } from './buildExercisePayload';
import { AddToPatientModalProps } from './types';

export const AddExerciseToPatientModal = ({
  content,
  open,
  onClose,
  id,
}: AddToPatientModalProps<Exercise>) => {
  const { regimenType, therapyType } = DefaultAddExerciseToPatientSettings;
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { mutateAsync: addExerciseToPatient } = useAddExerciseToPatient();
  const [payload, setPayload] = useState<AddExerciseToPatientPayload>(
    buildExercisePayload(id, '', content),
  );
  const [payloadContentId, setPayloadContentId] = useState<number | null>(
    content ? id : null,
  );
  const formPayload =
    open && content && payloadContentId !== id
      ? buildExercisePayload(id, '', content)
      : payload;

  React.useEffect(() => {
    if (open) {
      setShowSuccess(false);
      setErrorMessage('');
      setPayload(buildExercisePayload(id, '', content));
      setPayloadContentId(id);
    } else {
      setPayloadContentId(null);
    }
  }, [content, id, open, regimenType, therapyType]);

  const close = () => {
    onClose();
    setPayload(DefaultAddExerciseToPatientSettings);
    setPayloadContentId(null);
  };

  const formatErrorMessage = (errorMessage: string) => {
    const regex =
      /patient with id '\d+' already has content type '(\w+)' for content with id '\d+'/i;
    const match = errorMessage.match(regex);
    if (match) {
      const contentType = match[1];
      if (content.title) {
        return `Patient already has ${contentType.toLowerCase()} '${content.title}'`;
      }
    }
    return errorMessage;
  };

  const onUpdate = async (key: any, arg: any) => {
    setPayload((current) => ({
      ...current,
      contentId: id,
      [key]: arg,
    }));
  };

  const onSubmit = () => {
    addExerciseToPatient([
      {
        ...payload,
        instructionList: normalizePatientInstructions(payload.instructionList),
      },
    ] as AddExerciseToPatientPayload[])
      .then(() => {
        setShowSuccess(true);
        setTimeout(() => onClose(), 1500);
      })
      .catch((error) => {
        const serverMessage = error?.response?.data?.message;
        if (serverMessage) {
          setErrorMessage(formatErrorMessage(serverMessage));
        } else if (error.response && error.response.status === 403) {
          setErrorMessage(
            'Unexpected error occured. Please close the modal and try again.',
          );
        } else if (error.message) {
          setErrorMessage(formatErrorMessage(error.message));
        } else {
          setErrorMessage('Unexpected error. Please Try again.');
        }
      });
  };

  return (
    content && (
      <BaseModal open={open} onClose={close} title={'Add To A Patient'}>
        <AddExerciseToPatientForm
          initValue={formPayload}
          onBack={close}
          onSubmit={() => onSubmit()}
          onUpdate={onUpdate}
        />
        <AlertMessage
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
          message={`${content.title} successfully add to patient`}
        />
        <AlertMessage
          open={!!errorMessage}
          onClose={() => setErrorMessage('')}
          severity={SEVERITY.error}
          message={errorMessage}
        />
      </BaseModal>
    )
  );
};
